import React, { useState } from "react"
import { Form, FormGroup, Input, Alert, Label } from "reactstrap"
import validateInput from "../../view/shared/validations/signUp"
import { Trans, useLingui } from "@lingui/react"
import { loadingToggleState } from "../../store/actions/signUp"
import { useSelector, useDispatch } from "react-redux"
import { createBrowserHistory } from "history"
import { toast } from "react-toastify"
import "react-dates/initialize"
import "react-dates/lib/css/_datepicker.css"
import moment from "moment"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"
import ar from "react-phone-input-2/lang/ar.json"
import MultipleSelect from "../layout/MulitpleSelect"
import { defaultDate } from "../../view/shared/utilities"
import { arabicToInt } from "../../view/shared/utilities/arabicNumParser"
import StellarLoader from "../layout/StellarLoader"
import { AuthAPI } from "../../store/api"
import { v4 as uuid } from "uuid"
import { loginAction } from "../../store/actions/auth"

const history = createBrowserHistory({ forceRefresh: true })

const SuccessMessage = () => {
  return (
    <div style={loadingStyle}>
      <Trans id="account.created.successfully" />
    </div>
  )
}

const ErrorMessage = () => {
  return (
    <div style={loadingStyle}>
      <Trans id="error" />
    </div>
  )
}

const SignUpForm = () => {
  const { i18n } = useLingui()

  const [state, setState] = useState({
    full_name: "",
    phone_number: "",
    date_of_birth: defaultDate(),
    pin: "",
    confirm_pin: "",
    errors: {},
    isLoaded: false,
    bounce: "",
    gender: "",
    nationalities: [],
    lang: i18n.locale,
    buttonDisable: false,
    loading: true,
    _country: localStorage.getItem("_country") || "GH",
  })

  const dispatch = useDispatch()
  const { serverError, isCreating, showLoading } = useSelector((state) => ({
    serverError: state.createUserError,
    isCreating: state.isCreatingUser,
    showLoading: state.loadingToggleState,
  }))

  const notify = (msg) => toast.info(msg)

  const onChange = (e) => {
    setState((prevState) => ({
      ...prevState,
      buttonDisable: false,
      [e.target.name]: e.target.value,
    }))
  }

  const isValid = () => {
    const { errors, isValid } = validateInput(state)
    if (!isValid) {
      setState((prevState) => ({ ...prevState, errors }))
    }
    return isValid
  }

  const onSubmit = (e) => {
    e.preventDefault()
    if (isValid()) {
      dispatch(loadingToggleState(true))
      setState((prevState) => ({ ...prevState, buttonDisable: true, errors: {} }))
      signUpAction(
        state.phone_number,
        state.gender,
        state.nationalities,
        arabicToInt(state.pin),
        state.full_name,
        state.date_of_birth,
      )
        .then(() => {
          if (window.REACT_APP_PWA_APP_ENV === "production") {
            setTimeout(() => {
              history.push({
                pathname: `/ValidatePhone`,
              })
            }, 500)
          } else {
            dispatch(loginAction(state.phone_number, state.pin))
            notify(<SuccessMessage />)
            dispatch(loadingToggleState(false))
            setTimeout(() => {
              history.push({
                pathname: `/`,
              })
            }, 1500)
          }
        })
        .catch((error) => {
          dispatch(loadingToggleState(false))
          setState((prevState) => ({ ...prevState, buttonDisable: false }))
          const { response } = error
          if (!response || !response.data) {
            return
          }
          setState((prevState) => ({
            ...prevState,
            bounce: error.response.data.detail,
          }))
          notify(<ErrorMessage />)
        })
    }
  }

  const signUpAction = async (
    phone_number,
    gender,
    nationalities,
    pin,
    full_name,
    date_of_birth,
  ) => {
    const email = "+" + phone_number + "@hive.com"
    const finalDate = date_of_birth === null ? new Date() : new Date(date_of_birth)
    const dob = moment(finalDate).format("YYYY-MM-DD")
    const url = `/users?app=vsla&requestId=${uuid()}`

    const data = {
      full_name,
      gender,
      nationalities,
      email,
      password: pin,
      phone_number: `+${phone_number}`,
      date_of_birth: dob,
    }
    return await AuthAPI.post(url, data)
  }

  const headerStyle = {
    color: "#E36F1E",
    fontFamily: "quicksand",
    fontWeight: "bolder",
  }

  const formStyle = {
    flex: "10",
    padding: "5px",
    fontFamily: "Fira Sans",
    fontSize: "17px",
    height: "50px",
  }

  const errorStyle = {
    fontFamily: "Fira Sans",
    fontSize: "17px",
    color: "red",
  }

  const { errors, bounce, lang, _country } = state,
    en = false

  if (showLoading.loadingToggleState || (isCreating && !serverError)) {
    return <StellarLoader />
  }

  return (
    <div>
      <div
        style={{
          backgroundColor: "white",
          padding: ".5rem",
          textAlign: "center",
        }}
      >
        <h4 style={headerStyle}>
          <Trans id="signup" />
        </h4>
      </div>
      <Form onSubmit={onSubmit} noValidate>
        <FormGroup>
          <Label
            className={
              i18n.locale === "ar" ? "float-right" : ""
            }
            style={loadingStyle}
          >
            <Trans id="full.name" />
          </Label>
          <Input
            type="text"
            name="full_name"
            style={formStyle}
            placeholder={i18n._("Full name")}
            onChange={onChange}
            error={errors.full_name}
            value={state.full_name}
          />
          <span name="name_validation" style={errorStyle}>
            {errors.full_name}
          </span>
        </FormGroup>
        <FormGroup
          style={{
            direction: "ltr",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Label
            style={{
              ...loadingStyle,
              textAlign:
                i18n.locale === "ar" ? "right" : "left",
              width: "100%",
            }}
          >
            <Trans id="phone.number" />
          </Label>
          <PhoneInput
            inputClass="phone_number"
            country={_country.toLocaleLowerCase()}
            enableSearch
            disableSearchIcon
            placeholder={i18n._("Phone number")}
            error={errors.phone_number}
            style={loadingStyle}
            inputStyle={{
              flex: "10",
              fontFamily: "Fira Sans",
              height: "50px",
              width: "100%",
              fontSize: "20px",
            }}
            value={state.phone_number}
            onChange={(phone_number) =>
              setState((prevState) => ({ ...prevState, phone_number }))
            }
            limitMaxLength={true}
            localization={lang === "ar" ? ar : en}
          />
          {bounce && <span style={errorStyle}>{bounce}</span>}
          <span name="phone_validation" style={errorStyle}>
            {errors.phone_number}
          </span>
        </FormGroup>
        <FormGroup>
          <Label
            className={
              i18n.locale === "ar" ? "float-right" : ""
            }
            style={loadingStyle}
          >
            <Trans id="date.of.birth" />
          </Label>
          <Input
            type="date"
            name="date_of_birth"
            placeholder={i18n._("Date of birth")}
            selected={state.date_of_birth}
            style={formStyle}
            onChange={onChange}
            defaultValue={defaultDate()}
            numberofmonths={1}
            max={defaultDate()}
          />
          <span style={errorStyle}>{errors.date_of_birth}</span>
        </FormGroup>
        {["ar", "lb"].includes(state.lang) && (
          <MultipleSelect
            title={i18n._("Nationality")}
            preSetItems={state.nationalities}
            callback={(items) => {
              setState((prevState) => ({ ...prevState, nationalities: items }))
            }}
            placeholderOption={i18n._("Select Nationalities")}
            options={[
              {
                value: "Lebanon",
                title: "Lebanon",
              },
              {
                value: "Syria",
                title: "Syria",
              },
              {
                value: "Palestine",
                title: "Palestine",
              },
            ]}
          />
        )}
        <FormGroup>
          <Label
            className={
              i18n.locale === "ar" ? "float-right" : ""
            }
            style={loadingStyle}
          >
            <Trans id="gender" />
          </Label>
          <Input
            id="gender"
            type="select"
            name="gender"
            style={formStyle}
            onChange={onChange}
            value={state.gender}
          >
            <option disabled value="">
              <Trans id="gender" />
            </option>
            <option value="female">
              <Trans id="female" />
            </option>
            <option value="male">
              <Trans id="male" />
            </option>
          </Input>
          <span name="required_field_validation" style={errorStyle}>
            {errors.gender}
          </span>
        </FormGroup>
        <FormGroup>
          <Label
            className={
              i18n.locale === "ar" ? "float-right" : ""
            }
            style={loadingStyle}
          >
            <Trans id="pin" />
          </Label>
          <Input
            type="password"
            name="pin"
            style={formStyle}
            placeholder={i18n._("PIN")}
            onChange={onChange}
            error={errors.pin}
            value={state.pin}
          />
          <span name="pin_validation" style={errorStyle}>
            {errors.pin}
          </span>
        </FormGroup>
        <FormGroup>
          <Label
            className={
              i18n.locale === "ar" ? "float-right" : ""
            }
            style={loadingStyle}
          >
            <Trans id="confirm.pin" />
          </Label>
          <Input
            type="password"
            name="confirm_pin"
            style={formStyle}
            placeholder={i18n._("Confirm PIN")}
            onChange={onChange}
            error={errors.confirm_pin}
            value={state.confirm_pin}
          />
          <span name="confirm_pin_validation" style={errorStyle}>
            {errors.confirm_pin}
          </span>
        </FormGroup>

        <Alert color="warning">
          <span>
            <Trans id="age.confirmation" />
          </span>
        </Alert>
        <FormGroup>
          <Input
            type="submit"
            value={
              state.buttonDisable ? i18n._("Signing you up..") : i18n._("Sign Up")
            }
            name="submit"
            className="btn btn-primary"
            disabled={state.buttonDisable}
          />
        </FormGroup>
      </Form>
    </div>
  )
}

const loadingStyle = {
  fontFamily: "Fira Sans",
  fontSize: "20px",
}

export default SignUpForm
